// web/src/admin/adminUi.tsx — small building blocks the portal-spec-22 admin screens share: section cards, label/value
// rows, buttons (44 px), stage labels, date formatting and one "do this, toast, refresh" hook. Admin-only screens, so
// the words can be operator words (slugs, statuses), but every control stays phone-sized.
import { useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { SiteStage } from '../lib/types';
import { toast } from '../components/Toast';
import { apiErrorText } from '../lib/errors';
import { Icon } from '../components/Icon';

export const STAGE_LABEL: Record<SiteStage, string> = {
  demo: 'Demo', sold: 'Sold', finishing: 'Finishing', going_live: 'Going live', live: 'Live', paused: 'Paused',
  offboarding: 'Ending', legacy: 'Legacy', archived: 'Archived', offboarded: 'Gone',
};
export const STAGE_TONE: Record<SiteStage, string> = {
  demo: 'border-info text-info', sold: 'border-run text-run', finishing: 'border-run text-run', going_live: 'border-run text-run',
  live: 'border-success text-success', paused: 'border-danger text-danger', offboarding: 'border-warn text-warn',
  legacy: 'border-warn text-warn', archived: 'border-hair text-muted', offboarded: 'border-hair text-muted',
};

export function StagePill({ stage }: { stage: SiteStage }) {
  return <span className={`inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-xs ${STAGE_TONE[stage]}`}>{STAGE_LABEL[stage]}</span>;
}

/** "Oct 19, 2026" (or with the time). Admin screens show Mountain time, like run days and the auto-yes day. */
export function fmtDate(iso: string | null | undefined, withTime = false): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'America/Denver',
    ...(withTime ? { hour: 'numeric', minute: '2-digit' } : {}) });
}
export function ago(iso: string | null | undefined): string {
  if (!iso) return '—';
  const m = (Date.now() - Date.parse(iso)) / 60_000;
  if (m < 1) return 'just now';
  if (m < 60) return `${Math.floor(m)} min ago`;
  if (m < 48 * 60) return `${Math.floor(m / 60)} h ago`;
  return `${Math.floor(m / 1440)} days ago`;
}

export function Section({ title, id, action, children }: { title: string; id?: string; action?: ReactNode; children: ReactNode }) {
  return (
    <section id={id} className="min-w-0 rounded-lg border border-hair bg-surface p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-head text-sm text-muted">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

export function Rows({ children }: { children: ReactNode }) {
  return <dl className="divide-y divide-hair text-sm">{children}</dl>;
}
export function Row({ k, children }: { k: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 items-start justify-between gap-3 py-2">
      <dt className="shrink-0 text-muted">{k}</dt>
      <dd className="min-w-0 break-words text-right text-ink">{children}</dd>
    </div>
  );
}

type BtnKind = 'primary' | 'plain' | 'danger' | 'quiet';
const BTN: Record<BtnKind, string> = {
  primary: 'bg-run font-medium text-runink',
  plain: 'border border-hair text-ink',
  danger: 'border border-danger text-danger',
  quiet: 'text-muted underline',
};
export function Btn({ kind = 'plain', icon, children, className = '', ...rest }:
  { kind?: BtnKind; icon?: string; children: ReactNode; className?: string } & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button type="button" {...rest}
      className={`inline-flex min-h-11 items-center justify-center gap-2 rounded-md px-4 text-sm disabled:opacity-50 ${BTN[kind]} ${className}`}>
      {icon && <Icon name={icon} size={15} />}{children}
    </button>
  );
}

export const inputCls = 'min-h-11 w-full min-w-0 rounded-md border border-hair bg-bg px-3 text-sm text-ink';

/** Run an admin action: busy flag, a toast either way, then refresh the queries the screens read. */
export function useAction() {
  const qc = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);
  const run = async <T,>(key: string, fn: () => Promise<T>, ok: string | ((r: T) => string), fail = 'That didn’t work.'): Promise<T | null> => {
    setBusy(key);
    try {
      const r = await fn();
      toast({ kind: 'success', message: typeof ok === 'function' ? ok(r) : ok });
      void qc.invalidateQueries({ predicate: (q) => String(q.queryKey[0] ?? '').startsWith('admin') });
      return r;
    } catch (e) {
      toast({ kind: 'error', message: apiErrorText(e, fail) });
      return null;
    } finally { setBusy(null); }
  };
  return { busy, run };
}

/** A count pill for nav items and tiles (hidden at 0). */
export function CountPill({ n, tone = 'run' }: { n: number | undefined; tone?: 'run' | 'danger' }) {
  if (!n) return null;
  return <span className={`ml-auto inline-flex min-w-[1.25rem] items-center justify-center rounded-full px-1.5 text-[11px] font-medium ${tone === 'danger' ? 'bg-danger text-runink' : 'bg-run text-runink'}`}>{n > 99 ? '99+' : n}</span>;
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="rounded-lg border border-hair bg-surface/40 px-4 py-6 text-center text-sm text-muted">{children}</p>;
}

export function Collapsible({ title, count, defaultOpen = false, children }: { title: string; count?: number; defaultOpen?: boolean; children: ReactNode }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="min-w-0">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
        className="flex min-h-11 w-full items-center justify-between gap-2 rounded-md px-1 text-left text-sm text-muted">
        <span>{title}{count !== undefined ? ` (${count})` : ''}</span>
        <Icon name="chevron-right" size={16} className={`transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && <div className="mt-2">{children}</div>}
    </div>
  );
}
